import { useState, useEffect } from 'react';
import { useDashboardStore } from '@/features/dashboard/stores/dashboard.store';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';
import { useDashboardPrerequisites } from '@/features/dashboard/hooks/use-dashboard-prerequisites';
import { useRecordsSearch } from '@/features/dashboard/hooks/use-records-search';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import { Copy, AlertCircle } from 'lucide-react';
import { Checkbox } from '@/features/ui/components/Checkbox';
import { LoadingSpinner } from '@/features/help/components/LoadingSpinner';
import { MaterialTooltip } from '@/features/ui/components/MaterialTooltip';
import { MaterialSelect } from '@/features/ui/components/MaterialSelect';
import { MaterialAutocomplete } from '@/features/ui/components/MaterialAutocomplete';
import { FieldMappingSummary } from '@/features/form-builder/types';
import { InitialSetupModal } from './InitialSetupModal';

export const Dashboard = () => {
  const {
    selectedFieldMappingId,
    selectedRecordId,
    loading,
    selectFieldMapping,
    setSelectedRecordId,
    generateApplicationUrlForSelectedMapping,
  } = useDashboardStore();

  const { mappingSummaries } = useMappingsStore();

  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [pdfOnly, setPdfOnly] = useState(false);

  // State for the initial setup modal
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [isInitialSetupModalOpen, setIsInitialSetupModalOpen] = useState(false);

  // Get prerequisite status
  const { dashboardInitialized, prerequisitesMet } = useDashboardPrerequisites();

  // Use TanStack Query for records search
  const { records, setSearchQuery, isLoading: isLoadingRecords } = useRecordsSearch({
    mappingId: selectedFieldMappingId,
    enabled: !!selectedFieldMappingId,
  });

  // Auto-select first field mapping when available mappings are loaded
  useEffect(() => {
    if (mappingSummaries.length > 0 && !selectedFieldMappingId) {
      const firstMapping = mappingSummaries[0];
      logger.debug('Dashboard', 'Auto-selecting first available field mapping', {
        mappingId: firstMapping.mapping_id,
        mappingName: firstMapping.mapping_name,
      });
      selectFieldMapping(firstMapping.mapping_id, firstMapping.short_id);
    }
  }, [mappingSummaries, selectedFieldMappingId, selectFieldMapping]);

  // Effect to show the initial setup modal once if needed
  useEffect(() => {
    logger.debug('Dashboard', 'Checking initial setup modal conditions', {
      dashboardInitialized,
      prerequisitesMet,
      initialCheckDone,
    });

    // Condition 1: Only run check after initialization and only once
    if (dashboardInitialized && !initialCheckDone) {
      // Condition 2: If prerequisites are not met, open the modal
      if (!prerequisitesMet) {
        logger.info('Dashboard', 'Initial setup prerequisites not met. Opening setup modal.');
        setIsInitialSetupModalOpen(true);
      }
      // Mark check as done regardless to prevent re-opening on navigation
      setInitialCheckDone(true);
    }
  }, [dashboardInitialized, prerequisitesMet, initialCheckDone]);

  const handleCopyWithFeedback = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(successMessage);
      toast.success(successMessage);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      logger.error('Dashboard', 'Failed to copy to clipboard', {
        error,
      });
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCopyBaseUrl = () => {
    const baseUrl = generateApplicationUrlForSelectedMapping();
    if (baseUrl) {
      handleCopyWithFeedback(baseUrl, 'Base URL copied to clipboard');
    } else {
      toast.error('Please select a field mapping first');
    }
  };

  const handleCopyRecordUrl = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!selectedRecordId || !selectedFieldMappingId) return;

    let recordUrl = generateApplicationUrlForSelectedMapping(selectedRecordId);
    if (recordUrl && pdfOnly) {
      recordUrl += '?pdfOnly=1';
    }
    if (recordUrl) {
      const message = pdfOnly ? 'PDF-only record URL copied to clipboard' : 'Record URL copied to clipboard';
      handleCopyWithFeedback(recordUrl, message);
    }
  };



  // Get the currently selected record
  const selectedRecord = records.find((record: any) => record.id === selectedRecordId);

  return (
    <>
      <InitialSetupModal
        isOpen={isInitialSetupModalOpen}
        onClose={() => setIsInitialSetupModalOpen(false)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8 min-w-0 bg-gray-50">
        {/* Main Content */}
        <div className="grid grid-cols-1 gap-8">
          <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-8 shadow-sm">
            {/* Quick Link Generation */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-extrabold text-blue-500">Generate Link</h2>
                <MaterialTooltip 
                  title="Copy the base application link below. To use in your marketing materials, append /{recordId} to the end of this URL, where recordId is the unique identifier for each record."
                  placement="bottom"
                  leaveTouchDelay={7000}
                >
                  <AlertCircle className="h-5 w-5 text-gray-400 cursor-help" />
                </MaterialTooltip>
              </div>
            </div>

            {/* Field Mapping Selection */}
            <div>
              <MaterialSelect
                label="Select Field Mapping"
                value={selectedFieldMappingId || ''}
                onChange={(value) => {
                  const selectedMapping = mappingSummaries.find((m) => m.mapping_id === value);
                  selectFieldMapping(value || null, selectedMapping?.short_id || null);
                }}
                options={mappingSummaries.map((mapping: FieldMappingSummary) => ({
                  value: mapping.mapping_id,
                  label: mapping.mapping_name,
                }))}
                placeholder="Select a field mapping..."
                className="mb-2"
              />
            </div>

            {/* Base URL */}
            <div className="grid grid-cols-[1fr_auto] items-center bg-white border border-gray-200 rounded-lg relative min-w-0 overflow-hidden">
              <input
                type="text"
                readOnly
                data-testid="dashboard-base-url-input"
                value={
                  loading
                    ? 'Generating link...'
                    : selectedFieldMappingId
                      ? generateApplicationUrlForSelectedMapping() || ''
                      : 'Select a field mapping first'
                }
                className="px-4 py-3 bg-transparent text-gray-900 focus:outline-none text-lg min-w-0 truncate"
              />
              <button
                onClick={handleCopyBaseUrl}
                disabled={loading || !selectedFieldMappingId}
                className="p-3 hover:bg-gray-50 rounded-lg transition-colors group disabled:opacity-50 disabled:cursor-not-allowed relative"
                title="Copy base URL"
              >
                {loading ? (
                  <LoadingSpinner className="h-6 w-6" />
                ) : (
                  <Copy
                    className={`h-6 w-6 ${copySuccess ? 'text-green-500' : 'text-blue-500'} group-hover:text-blue-400`}
                  />
                )}
              </button>
            </div>

            {/* Search Autocomplete */}
            <div>
              <MaterialAutocomplete
                label="Search for a specific record"
                value={selectedRecord ? {
                  ...selectedRecord,
                  label: selectedRecord.name
                } : null}
                onChange={(record) => {
                  setSelectedRecordId(record?.id || null);
                  if (!record) {
                    setPdfOnly(false);
                  }
                }}
                onInputChange={(inputValue) => {
                  setSearchQuery(inputValue);
                }}
                options={records.map((record: any) => ({
                  ...record,
                  label: record.name
                }))}
                placeholder="Search records by name or email..."
                loading={isLoadingRecords}
                disabled={!selectedFieldMappingId}
                noOptionsText="No records found."
                freeSolo={false}
                className="mb-2"
              />
            </div>

            {/* PDF-only option for selected record */}
            {selectedRecord && selectedFieldMappingId && (
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <Checkbox
                    checked={pdfOnly}
                    onCheckedChange={(checked) => setPdfOnly(checked === true)}
                    className="border-gray-300 data-[state=unchecked]:bg-white"
                  />
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    PDF-only
                    <MaterialTooltip 
                      title="Generates PDF without updating CRM fields"
                      placement="top"
                      leaveTouchDelay={3000}
                    >
                      <AlertCircle className="h-3 w-3 text-gray-500 cursor-help" />
                    </MaterialTooltip>
                  </span>
                </label>
              </div>
            )}

            {/* Record URL Display */}
            {selectedRecord && selectedFieldMappingId && (
              <div className="grid grid-cols-[1fr_auto] items-center bg-white border border-gray-200 rounded-lg relative min-w-0 overflow-hidden">
                <input
                  type="text"
                  readOnly
                  value={
                    (() => {
                      const baseUrl = generateApplicationUrlForSelectedMapping(selectedRecord.id) || '';
                      return pdfOnly && baseUrl ? `${baseUrl}?pdfOnly=1` : baseUrl;
                    })()
                  }
                  className="px-4 py-3 bg-transparent text-gray-900 focus:outline-none min-w-0 truncate"
                />
                <button
                  onClick={handleCopyRecordUrl}
                  className="p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                  title="Copy record URL"
                >
                  <Copy className="h-6 w-6 text-blue-500 group-hover:text-blue-400" />
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};


